<script>
	import { createEventDispatcher, getContext, onMount } from 'svelte';

	import { keyToken } from './globals.js';
	import { formatAmount, formatPercent } from './components/formatting.js';
	import PurchaseDetails from './components/dialog/PurchaseDetails.svelte';
	import SortableTable from './components/table/SortableTable.svelte';
	import StockTableCell from './components/table/StockTableCell.svelte';

	const dispatch = createEventDispatcher();

	const { getToken } = getContext(keyToken);
	const token = getToken();

	export let updates;

	let stocks = [];
	let totalInitialValue;
	let totalMarketValue;
	let totalDelta;
	let totalDeltaPercent;
	let isin;
	let showHistoric = false;
	let stock = null;

	$: {
		if (updates != null && (updates.type === 'portfolio' || updates.type === 'prices')) {
			console.log('Received portfolio update:', updates);
		}

		loadPortfolio(showHistoric);
	}

	async function deletePortfolio() {
		if (!token) {
			console.warn('Not logged in!');
			return;
		}

		await fetch('https://ostium.ch/api/borsalino/delete_portfolio_details.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			},
			body: JSON.stringify({ isin: stock.isin, historic: showHistoric })
		}).then(result => {
			if (result.ok) {
				result.json().then(data => {
					console.log('Deleted asset from portfolio:', data);
					dispatch('message', {
						type: 'success',
						text: data.message
					});
				});
			} else {
				console.warn('Could not delete asset from portfolio.', result.statusText);
				result.json().then(data => {
					console.log('Error:', data);
					dispatch('message', {
						type: 'error',
						text: data.error != null ? data.error : data.warning
					});
				});
			}
			jQuery('#deletePortfolioDialog').modal('hide');
			loadPortfolio(showHistoric);
		});
	}

	async function loadPortfolio(historic) {
		if (!token) {
			console.warn('Not logged in!');
			return;
		}

		await fetch('https://ostium.ch/api/borsalino/get_portfolio.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			},
			body: JSON.stringify({ historic: historic })
		}).then(result => {
			if (result.ok) {
				console.log('Loaded portfolio data successfully.');
				result.json().then(data => {
					console.log(`Loaded ${data.length} portfolio record${data.length === 1 ? '' : 's'}.`);
					totalInitialValue = 0;
					totalMarketValue = 0;
					stocks = data.map(stock => {
						stock.market_value = stock.quantity * stock.last;
						stock.delta = stock.market_value - stock.initial_price;
						stock.delta_percent = stock.delta / stock.initial_price;
						totalInitialValue += stock.initial_price / stock.factor;
						totalMarketValue += stock.market_value / stock.factor;
						return stock;
					});
					totalDelta = totalMarketValue - totalInitialValue;
					totalDeltaPercent = totalDelta / totalInitialValue;
				});
			} else {
				console.warn('Could not load portfolio data.', result.statusText);
			}
		});
	}

	onMount(() => {
		jQuery('#deletePortfolioDialog').on('show.bs.modal', function (event) {
			let target =  event.relatedTarget;
			if (target != null && target.dataset != null && target.dataset['isin'] != null) {
				isin = target.dataset['isin'];
				stock = stocks.find(item => item.isin == isin);
				console.log('Delete alarm with ISIN ' + isin + (showHistoric ? ' with historic records' : ''), stock);
			}
		});
	});
</script>

<div class="container-fluid">
	<div class="row">
		<div class="col">
			<div class="row align-items-end no-gutters">
				<div class="col">
					<h2 class="mt-4 mb-2"><a class="text-light" href="#collapsePortfolio" data-toggle="collapse" aria-expanded="true">Portfolio</a></h2>
				</div>
				<div class="col-md-auto form-check mb-2 text-right">
					<input class="form-check-input ml-0" type="checkbox" id="showHistoric" bind:checked={showHistoric}>
					<label class="form-check-label ml-4" for="showHistoric">Show historic trades</label>
				</div>
			</div>
			<SortableTable id="collapsePortfolio" class="table table-sm table-dark table-striped" containerClass="collapse show" headClass="bg-secondary" footClass="bg-secondary" items={stocks}>
				<tr slot="thead">
					<th data-sort="name" data-sort-initial="ascending">Name</th>
					<th class="hide-lg" data-sort="symbol">Symbol</th>
					<th class="text-right" data-sort="quantity">Quantity</th>
					<th class="text-right" data-sort="price">Price</th>
					<th class="text-right" data-sort="last">Last</th>
					<th class="text-right" data-sort="initial_price">Initial Value</th>
					<th class="text-right" data-sort="market_value">Market Value</th>
					<th class="text-right" data-sort="delta">Diff</th>
					<th class="text-right" data-sort="delta_percent">Diff %</th>
					<th></th>
				</tr>
				<tr slot="tbody" let:item={stock}>
					<StockTableCell {stock} show='name' href="." target="#assetDetailsDialog"/>
					<StockTableCell {stock} show='symbol' class="hide-lg"/>
					<td class="text-right">{stock.quantity}</td>
					<StockTableCell {stock} show='price'/>
					<StockTableCell {stock} show='last'/>
					<StockTableCell {stock} show='initial_price'/>
					<StockTableCell {stock} show='market_value'/>
					<StockTableCell {stock} show='delta'/>
					<StockTableCell {stock} show='delta_percent'/>
					<td class="col-action">
						<div class="btn-group" role="group">
							<button type="button" class="btn btn-link text-light fas fa-ellipsis-v" data-toggle="dropdown"></button>
							<div class="dropdown-menu dropdown-menu-right">
								<a href="." class="dropdown-item" data-toggle="modal" data-target="#buySellDialog" data-isin={stock.isin}>Buy/Sell</a>
								<a href="." class="dropdown-item" data-toggle="modal" data-target="#purchaseDetailsDialog" data-isin={stock.isin}>Show details</a>
								<a href="." class="dropdown-item" data-toggle="modal" data-target="#deletePortfolioDialog" data-isin={stock.isin}>Delete</a>
							</div>
						</div>
					</td>
				</tr>
				<tr slot="tfoot">
					<th><a href="." class="text-light" data-toggle="modal" data-target="#assetDetailsDialog" data-isin="PORTFOLIO">Total</a></th>
					<th class="hide-lg"></th>
					<th></th>
					<th></th>
					<th></th>
					<th class="text-right">{formatAmount(totalInitialValue)}</th>
					<th class="text-right">{formatAmount(totalMarketValue)}</th>
					<th class="{'text-right ' + (totalDelta < 0 ? 'text-danger' : 'text-success')}">{totalDelta > 0 ? '+' : ''}{formatAmount(totalDelta)}</th>
					<th class="{'text-right ' + (totalDeltaPercent < 0 ? 'text-danger' : 'text-success')}">{formatPercent(totalDeltaPercent)}</th>
					<th></th>
				</tr>
			</SortableTable>
		</div>
	</div>
</div>

<div class="modal fade" id="deletePortfolioDialog" tabindex="-1" role="alert">
	<div class="modal-dialog modal-dialog-centered modal-lg" role="document">
		<div class="modal-content">
			<div class="modal-header bg-warning">
				<h5 class="modal-title text-dark">Caution</h5>
				<button type="button" class="close" data-dismiss="modal" aria-label="Close">
					<span aria-hidden="true">&times;</span>
				</button>
			</div>
			<div class="modal-body text-dark">
				{#if isin != null}
				Do you really want to remove {stock.symbol} ({stock.isin}) from the portfolio?
				{/if}
			</div>
			<div class="modal-footer">
				<button type="button" class="btn btn-danger" data-dismiss="modal">No, keep it!</button>
				<button type="button" class="btn btn-success" on:click|preventDefault={deletePortfolio}>Yes, delete it.</button>
			</div>
		</div>
	</div>
</div>

<PurchaseDetails {showHistoric} />
